import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import {
  FIRESTORE_V2_STORAGE_MARKER_FIELD,
  FIRESTORE_V2_STORAGE_MARKER_VALUE,
} from "../../data/remote/firestore/constants";
import { cloudTransport } from "../cloud";

const mapVesselDocument = (snapshot) => {
  const data = snapshot.data();

  return {
    id: snapshot.id,
    ...data,
    name:
      data.name ||
      data.configuration?.identifiers?.vesselName ||
      data.baseSettings?.shipName ||
      "Unknown Vessel",
  };
};

const isManagedV2Document = (document) =>
  document?.[FIRESTORE_V2_STORAGE_MARKER_FIELD] ===
  FIRESTORE_V2_STORAGE_MARKER_VALUE;

class VesselProvisioningRepository {
  constructor({ db }) {
    this.db = db;
  }

  async listAvailableVessels() {
    const vesselsSnapshot = await getDocs(collection(this.db, "vessels"));
    return vesselsSnapshot.docs
      .map((snapshot) => ({
        id: snapshot.id,
        ...snapshot.data(),
      }))
      .filter(isManagedV2Document)
      .map((data) => ({
        id: data.id,
        name:
          data.name ||
          data.configuration?.identifiers?.vesselName ||
          "Unknown Vessel",
      }));
  }

  async getVesselDataById(vesselId) {
    const vesselSnapshot = await getDoc(doc(this.db, "vessels", vesselId));
    if (vesselSnapshot.exists()) {
      const vessel = mapVesselDocument(vesselSnapshot);
      if (!isManagedV2Document(vessel)) {
        throw new Error("Vessel document is not managed by OceanLogix v2");
      }
      return vessel;
    }

    throw new Error("Vessel document missing");
  }

  async getVesselParticularsByVesselId(vesselId) {
    const particularsSnapshot = await getDoc(
      doc(this.db, "vesselParticulars", vesselId)
    );

    if (!particularsSnapshot.exists()) {
      return null;
    }

    const particulars = {
      vesselId: particularsSnapshot.id,
      ...particularsSnapshot.data(),
    };

    if (!isManagedV2Document(particulars)) {
      throw new Error(
        "VesselParticulars document is not managed by OceanLogix v2"
      );
    }

    return particulars;
  }

  async listTanksByVesselId(vesselId) {
    const tanksQuery = query(
      collection(this.db, "tanks"),
      where("vesselId", "==", vesselId)
    );
    const snapshot = await getDocs(tanksQuery);

    return snapshot.docs
      .map((tankDoc) => ({
        id: tankDoc.id,
        ...tankDoc.data(),
      }))
      .filter(isManagedV2Document);
  }

  async getActiveVesselAccess(vesselId) {
    const accessQuery = query(
      collection(this.db, "vesselAccess"),
      where("vesselId", "==", vesselId),
      where("status", "==", "active")
    );
    const snapshot = await getDocs(accessQuery);

    return snapshot.docs.map((accessDoc) => ({
      id: accessDoc.id,
      ...accessDoc.data(),
    }));
  }

  async getUsersByIds(userIds) {
    const uniqueUserIds = [...new Set(userIds.filter(Boolean))];
    const userSnapshots = await Promise.all(
      uniqueUserIds.map((userId) => getDoc(doc(this.db, "users", userId)))
    );

    const users = [];
    const missingUserIds = [];

    userSnapshots.forEach((snapshot, index) => {
      if (!snapshot.exists()) {
        missingUserIds.push(uniqueUserIds[index]);
        return;
      }

      if (snapshot.id === "last_updated") {
        return;
      }

      users.push({
        id: snapshot.id,
        ...snapshot.data(),
      });
    });

    return {
      users,
      missingUserIds,
    };
  }
}

export const createVesselProvisioningRepository = () => {
  const { db } = cloudTransport.getAuthenticatedInstallation();
  return new VesselProvisioningRepository({ db });
};
